@if (!isAuthenticated) {
  <app-portal-navbar></app-portal-navbar>
}

<ion-content [fullscreen]="!isAuthenticated" [scrollEvents]="true">

  <!-- ═══ PAGE HEADER ═══ -->
  <div class="page-header" [class.page-header--public]="!isAuthenticated" [class.page-header--shell]="isAuthenticated">
    <div class="container">
      <p class="section-label">{{ 'mainauth.contact.title' | translate }}</p>
      <h1>{{ 'mainauth.contact.headingLine1' | translate }}&ngsp;<br><span class="accent">{{ 'mainauth.contact.headingAccent' | translate }}</span></h1>
      <p class="lead">
        {{ 'mainauth.contact.lead' | translate }}
      </p>
    </div>
  </div>

  <!-- ═══ MAIN LAYOUT ═══ -->
  <div class="main-wrap">
    <div class="container">
      <div class="layout">

        <!-- ── FORM COLUMN ── -->
        <div class="form-col">

          <!-- Existing customers: a ticket keeps its history, this form lands in the sales inbox. -->
          @if (hasSupport && !success) {
            <div class="support-hint">
              <ion-icon name="chatbubbles-outline"></ion-icon>
              <p>
                {{ 'mainauth.contact.supportHintBody' | translate }}
                <a routerLink="/support">{{ 'mainauth.contact.supportHintLink' | translate }}</a>
              </p>
            </div>
          }

          <!-- The selection only matters while the form is open; next to the success message it just confuses. -->
          @if (!success) {
          <!-- Type selector -->
          <div class="type-pills" role="group" [attr.aria-label]="'mainauth.contact.typeGroupLabel' | translate">
            @for (t of contactTypes; track t) {
              <button
                class="type-pill"
                [class.active]="selectedType.id === t.id"
                [attr.aria-pressed]="selectedType.id === t.id"
                (click)="selectType(t)"
                type="button">
                <ion-icon [name]="t.icon"></ion-icon>
                {{ t.labelKey | translate }}
              </button>
            }
          </div>

          <!-- Type hint -->
          <div class="type-hint">
            <ion-icon name="information-circle-outline"></ion-icon>
            {{ selectedType.hintKey | translate }}
          </div>

          <!-- Product selector (trial enquiries only) -->
          @if (showProductSelector) {
            <div class="product-selector">
              <p class="selector-label">{{ 'mainauth.contact.productSelectorLabel' | translate }}</p>
              <div class="product-pills" role="group" [attr.aria-label]="'mainauth.contact.productSelectorLabel' | translate">
                @for (p of products; track p) {
                  <button
                    class="product-pill"
                    [class.active]="selectedProduct.id === p.id"
                    [attr.aria-pressed]="selectedProduct.id === p.id"
                    (click)="selectProduct(p)"
                    type="button">
                    @if (p.iconSrc) {
                      <img [src]="p.iconSrc" alt="" class="product-pill__mark" />
                    }
                    @if (!p.iconSrc) {
                      <ion-icon [name]="p.icon"></ion-icon>
                    }
                    {{ p.labelKey | translate }}
                  </button>
                }
              </div>
            </div>
          }
          }

          <!-- Success state -->
          @if (success) {
            <div class="success-box">
              <ion-icon name="checkmark-circle-outline"></ion-icon>
              <div>
                <strong>{{ 'mainauth.contact.successTitle' | translate }}</strong>
                <p>{{ 'mainauth.contact.successBody' | translate }}</p>
              </div>
            </div>
          }

          <!-- Form -->
          @if (!success) {
            <form (ngSubmit)="submit()">
              <div class="field-row">
                <div class="field">
                  <label class="field-label">{{ 'mainauth.contact.fieldName' | translate }} <span class="req">*</span></label>
                  <ion-input
                    class="field-input"
                    [(ngModel)]="form.name"
                    name="name"
                    type="text"
                    autocomplete="name"
                    maxlength="100"
                    [attr.aria-label]="'mainauth.contact.fieldName' | translate"
                    aria-required="true"
                    placeholder="Max Muster"
                    required>
                  </ion-input>
                </div>
                <div class="field">
                  <label class="field-label"
                    [appTooltip]="'mainauth.contact.tt.email' | translate">{{ 'mainauth.contact.fieldEmail' | translate }} <span class="req">*</span></label>
                    <ion-input
                      class="field-input"
                      [(ngModel)]="form.email"
                      name="email"
                      type="email"
                      autocomplete="email"
                      maxlength="256"
                      [attr.aria-label]="'mainauth.contact.fieldEmail' | translate"
                      aria-required="true"
                      placeholder="max@restaurant.ch"
                      required>
                    </ion-input>
                  </div>
                </div>
                <div class="field-row">
                  <div class="field">
                    <!-- Feeds the trial provisioning in the admin inbox, so it is mandatory for
                    trial enquiries (see ContactPage.companyRequired). -->
                    <label class="field-label"
                      [appTooltip]="'mainauth.contact.tt.company' | translate">{{ 'mainauth.contact.fieldCompany' | translate }}
                      @if (companyRequired) { <span class="req">*</span> } @else { <span class="opt">{{ 'mainauth.contact.optional' | translate }}</span> }
                    </label>
                    <ion-input
                      class="field-input"
                      [(ngModel)]="form.company"
                      name="company"
                      type="text"
                      autocomplete="organization"
                      maxlength="200"
                      [attr.aria-label]="'mainauth.contact.fieldCompany' | translate"
                      [attr.aria-required]="companyRequired"
                      [placeholder]="'mainauth.contact.companyPlaceholder' | translate"
                      [required]="companyRequired">
                    </ion-input>
                  </div>
                  <div class="field">
                    <label class="field-label"
                    [appTooltip]="'mainauth.contact.tt.subject' | translate">{{ 'mainauth.contact.fieldSubject' | translate }} <span class="opt">{{ 'mainauth.contact.optional' | translate }}</span></label>
                    <ion-input
                      class="field-input"
                      [(ngModel)]="form.subject"
                      name="subject"
                      type="text"
                      autocomplete="off"
                      maxlength="300"
                      [attr.aria-label]="'mainauth.contact.fieldSubject' | translate"
                      [placeholder]="(selectedType.subjectKey | translate) + (showProductSelector && selectedProduct.id !== 'all' ? ': ' + (selectedProduct.labelKey | translate) : '')">
                    </ion-input>
                  </div>
                </div>
                <div class="field">
                  <label class="field-label"
                    [appTooltip]="'mainauth.contact.tt.message' | translate">{{ 'mainauth.contact.fieldMessage' | translate }} <span class="req">*</span></label>
                    <ion-textarea
                      class="field-input field-textarea"
                      [(ngModel)]="form.message"
                      name="message"
                      autocomplete="off"
                      maxlength="2000"
                      [counter]="true"
                      [attr.aria-label]="'mainauth.contact.fieldMessage' | translate"
                      aria-required="true"
                      [placeholder]="'mainauth.contact.messagePlaceholder' | translate"
                      rows="6"
                      required>
                    </ion-textarea>
                  </div>
                  <!-- Right above the button: that is where the eye is after pressing it. A box at the
                       top of the form was out of sight on a phone. -->
                  @if (error) {
                    <div class="error-box" role="alert">
                      <ion-icon name="alert-circle-outline"></ion-icon>
                      {{ error }}
                    </div>
                  }
                  <ion-button
                    expand="block"
                    type="submit"
                    class="submit-btn"
                    [disabled]="loading">
                    @if (loading) {
                      <ion-spinner name="crescent" slot="start"></ion-spinner>
                    }
                    {{ (loading ? 'mainauth.contact.sending' : 'mainauth.contact.submit') | translate }}
                    @if (!loading) {
                      <ion-icon name="arrow-forward-outline" slot="end"></ion-icon>
                    }
                  </ion-button>
                  <p class="response-note">
                    <ion-icon name="time-outline"></ion-icon>
                    {{ 'mainauth.contact.responseTimeShort' | translate }}
                  </p>
                  <p class="privacy-note">
                    <ion-icon name="lock-closed-outline"></ion-icon>
                    {{ 'mainauth.contact.privacyNote' | translate }}
                    <a routerLink="/privacy">{{ 'mainauth.contact.privacyLink' | translate }}</a>
                  </p>
                </form>
              }

            </div>

            <!-- ── SIDEBAR ── -->
            <aside class="sidebar">

              <div class="info-card">
                <div class="info-icon">
                  <ion-icon name="mail-outline"></ion-icon>
                </div>
                <h3>{{ 'mainauth.contact.emailCardTitle' | translate }}</h3>
                <p>{{ 'mainauth.contact.emailCardBody' | translate }}</p>
                <a href="mailto:info@gastro-management.ch" class="info-link">
                  info@gastro-management.ch
                </a>
              </div>

              <div class="info-card">
                <div class="info-icon">
                  <ion-icon name="time-outline"></ion-icon>
                </div>
                <h3>{{ 'mainauth.contact.responseTimeTitle' | translate }}</h3>
                <p>{{ 'mainauth.contact.responseTimeBody' | translate }}</p>
              </div>

              <div class="info-card highlight-card">
                <div class="info-icon accent-icon"
                  [appTooltip]="'mainauth.contact.tt.freeStart' | translate"
                  tooltipTheme="brand"
                  tooltipPosition="top">
                  <ion-icon name="rocket-outline"></ion-icon>
                </div>
                <h3>{{ 'mainauth.contact.freeStartTitle' | translate }}</h3>
                <p>
                  {{ 'mainauth.contact.freeStartBody' | translate }}
                </p>
                <div class="feature-list">
                  @for (f of ['mainauth.contact.featChroniq', 'mainauth.contact.featScreenDeck', 'mainauth.contact.featPortal', 'mainauth.contact.featOnboarding']; track f) {
                    <div class="feat">
                      <ion-icon name="checkmark-outline"></ion-icon>
                      {{ f | translate }}
                    </div>
                  }
                </div>
              </div>

            </aside>

          </div>
        </div>
      </div>

      <app-portal-footer></app-portal-footer>

    </ion-content>
